import { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from 'react-router-dom';
import MyProfilePage from './pages/MyProfilePage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MenuPage from './pages/MenuPage';
import ShopPage from './pages/ShopPage';
import ShopCategoryPage from './pages/ShopCategoryPage';
import ShopPageTCG from './pages/ShopPageTCG';
import ShopPageAccess from './pages/ShopPageAccess';
import ShopPageMisc from './pages/ShopPageMisc';
// import CommunityMarketPage from './pages/CommunityMarketPage';
// import CreateListingPage from './pages/CreateListingPage';e';
// import EditListingPage from './pages/EditListingPage';
// import ListingDetailPage from './pages/ListingDetailPage';
// import MyListingsPage from './pages/MyListingsPage';
// import MyPurchasesPage from './pages/MyPurchasesPage';
// import MySalesPage from './pages/MySalesPage';
import PurchaseHistoryPage from './pages/PurchaseHistoryPage';
import TournamentPage from './pages/TournamentPage';
import LeaderboardPage from './pages/LeaderboardPage';
import ToolPage from './pages/ToolPage';
import AdminDashboard from './pages/AdminDashboard';
import CardSearch from './pages/CardSearch';
import TimeLimitPage from './pages/TimeLimitPage';
import DuelistListPage from './pages/DuelistListPage';
import BGMManager from './components/BGMManager';
import PrivateAdminRoute from './components/PrivateAdminRoute';
import ProtectedRoute from './components/ProtectedRoute';
import SessionWarning from './components/SessionWarning';
import { getLoggedInUser, logoutUser } from './logic/loginLogic';
import { doc, getDoc } from 'firebase/firestore';
import { initializeSession } from './utils/sessionManager';
import { testSecurityHeaders } from './utils/securityHeadersTest';
import { db } from './firebase';
// import UserProfilePage from './pages/UserProfilePage';
import SettingsIcon from './assets/icons/SettingsIcon.svg';
import CloseBtnIcon from './assets/icons/Close BTN.svg';
import { useTranslation } from 'react-i18next';
import PublicProfilePage from './pages/PublicProfilePage';
import AdminTournamentPage from './pages/AdminTournamentPage';
import AdminTournamentArchive from './pages/AdminTournamentArchive';
import AdminDpPurchaseHistory from './pages/AdminDpPurchaseHistory';
import AdminTournamentParticipationTracker from './pages/AdminTournamentParticipationTracker';
import AdminLeaderboardArchive from './pages/AdminLeaderboardArchive';
import LeaderboardArchiveListPage from './pages/LeaderboardArchiveListPage';
import LeaderboardArchivePage from './pages/LeaderboardArchivePage';
import AdminRewardSystem from './pages/AdminRewardSystem';
import DuelPage from './pages/DuelPage'; // adjust path if needed
import DeckPage from './pages/DeckPage';
import DeckBuilderPage from './pages/DeckBuilderPage';
import RoomPage from './pages/RoomPage';
import PvpOnline from './pages/PvpOnline';
// import RotatedCardTest from './components/RotatedCardTest'; // ✅ correct path to .jsx file
import PlayerTournamentPage from './pages/PlayerTournamentPage'; // adjust path if needed
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import AdminQuestPage from './pages/AdminQuestPage';
import AdminAchievementPage from './pages/AdminAchievementPage';
import AdminUserQuestPage from './pages/AdminUserQuestPage';
import useWakeLock from './hooks/useWakeLock';
import AdminShopManager from './pages/AdminShopManager';
import AdminManageUsers from './pages/AdminManageUsers';
import AdminManagePurchases from './pages/AdminManagePurchases';
import AdminLogViewer from './pages/AdminLogViewer';
import TournamentNotificationTest from './pages/TournamentNotificationTest';
import SecurityComplianceDashboard from './pages/SecurityComplianceDashboard';
import { NotificationProvider } from './contexts/NotificationContext';
import { UnreadMessagesProvider } from './contexts/UnreadMessagesContext';
import GlobalNotification from './components/GlobalNotification';
import GlobalUnreadBanner from './components/GlobalUnreadBanner';
import BackButtonHandler from './components/BackButtonHandler';
import { ToastContainer } from 'react-toastify';
import DuelSimulator from './pages/DuelSimulator'; // adjust path if needed
import DuelDragPage from './pages/DuelDragPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import { checkNotificationStatus } from './utils/notificationManager';
import VersionGate from './components/VersionGate';
import NotificationSettings from './components/NotificationSettings';
// GoogleAuthenticatorSettings moved to AccountSettingsPage
// import TOTPTest from './components/TOTPTest';
import { setupNotificationListener, checkAndReinitializeNotifications, initializeNotifications, forceRefreshNotifications } from './utils/notificationManager';

import 'react-toastify/dist/ReactToastify.css';


function GlobalSettingsModal({ show, setShow }) {
  const [muted, setMuted] = useState(() => window.getBGMMuted?.() ?? false);
  const [volume, setVolume] = useState(() => {
    return parseFloat(localStorage.getItem('bgmVolume')) || 0.5;
  });
  const [notificationStatus, setNotificationStatus] = useState('Checking...');
  // Removed currentView state since TOTP is now in Account Settings
  // const [showTOTPTest, setShowTOTPTest] = useState(false);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getLoggedInUser();
  const isLoggedIn = !!user;

  const handleLogout = async () => {
    if (!isLoggedIn) return;
    
    try {
      // Clear FCM token and logout properly
      await logoutUser();
      console.log('✅ User logged out successfully');
    } catch (error) {
      console.error('❌ Error during logout:', error);
    }
    
    document.body.style.overflow = '';
    setShow(false);
    navigate('/');
  };

  const handleNotificationTroubleshoot = async () => {
    if (!isLoggedIn) return;
    
    setNotificationStatus('Refreshing...');
    try {
      const result = await forceRefreshNotifications(user.uid);
      if (result.success) {
        setNotificationStatus('✅ Fixed! Notifications should work now.');
      } else {
        setNotificationStatus(`❌ Failed: ${result.error}`);
      }
    } catch (error) {
      setNotificationStatus(`❌ Error: ${error.message}`);
    }
    
    // Reset status after 5 seconds
    setTimeout(() => setNotificationStatus('Checking...'), 5000);
  };

  useEffect(() => {
    if (show) {
      setMuted(window.getBGMMuted?.() ?? false);
    }
  }, [show]);

  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : 'auto';
  }, [show]);

  // Handle click outside modal to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setShow(false);
    }
  };

  return show ? (
    <div className="setting-panel-overlay" onClick={handleOverlayClick}>
      <div className="setting-panel">
      <div className="settings-title">{t('settings.title')}</div>
      <a
        className="contact-button"
        href="https://m.me/YGOMadagascarEsport"
        target="_blank"
        rel="noopener noreferrer"
      >
        {t('shopCategory.contactButton')}
      </a>
      <img
        src={CloseBtnIcon}
        alt="Close"
        className="close-btn"
        onClick={() => setShow(false)}
      />

      <div className="volume-control">
        <label>{t('settings.volume')}</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={(e) => {
            const newVolume = parseFloat(e.target.value);
            setVolume(newVolume);
            localStorage.setItem('bgmVolume', newVolume);
            window.setBGMVolume?.(newVolume);
          }}
        />
      </div>
      <div
        className="mute-toggle-in-modal"
        onClick={() => {
          window.toggleBGMMute?.();
          setMuted(prev => !prev);
        }}
      >
        {muted ? '🔇 Mute' : '🔊 Unmute'}
      </div>

      <div className="language-select">
        <label>{t('settings.language')}</label>
        <select
          onChange={(e) => i18n.changeLanguage(e.target.value)}
          value={i18n.language}
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
        </select>
      </div>

      {/* Notification Toggle */}
      {isLoggedIn && <NotificationSettings />}
      

      <div
        className={`logout-btn ${!isLoggedIn ? 'disabled' : ''}`}
        onClick={handleLogout}
      >
        {t('settings.logout')}
      </div>
      </div>
    </div>
  ) : null;
}

// Account Settings Toggle Component removed - now accessible from Main Menu

// Notification status monitoring function
const setupNotificationStatusMonitoring = () => {
  try {
    console.log('🔍 Setting up notification status monitoring...');
    
    // Check notification status every 30 minutes
    const checkInterval = setInterval(() => {
      const status = checkNotificationStatus();
      if (status) {
        console.log('📊 Notification Status:', status);
        
        // Show warning if permissions are denied
        if (status.permission === 'denied') {
          console.warn('⚠️ Notification permission denied - notifications will not work');
        }
        
        // Show warning if service worker is not available
        if (status.serviceWorker === 'Not available') {
          console.warn('⚠️ Service Worker not available - background notifications may not work');
        }
      }
    }, 30 * 60 * 1000); // 30 minutes
    
    // Store interval for cleanup
    window.notificationStatusInterval = checkInterval;
    
    // Initial check
    setTimeout(() => {
      const initialStatus = checkNotificationStatus();
      console.log('📊 Initial Notification Status:', initialStatus);
    }, 5000); // Check after 5 seconds
    
    console.log('✅ Notification status monitoring setup completed');
  } catch (error) {
    console.error('❌ Error setting up notification status monitoring:', error);
  }
};

function App() {
  useWakeLock();
  const [showSettings, setShowSettings] = useState(false);
  const [cardSearchOpen, setCardSearchOpen] = useState(false); // ✅ NEW
  const [showGlobalTopButtons, setShowGlobalTopButtons] = useState(true);
  const [sessionUser, setSessionUser] = useState(() => getLoggedInUser());

  // Expose global access for Search Modal toggle
  useEffect(() => {
    window.openSettings = () => setShowSettings(true);
    window.setCardSearchOpen = setCardSearchOpen; // ✅ Allow global toggle
    window.setShowGlobalTopButtons = setShowGlobalTopButtons;
  }, []);

  // Keep session user in sync (login/logout without full refresh)
  useEffect(() => {
    const syncSessionUser = () => setSessionUser(getLoggedInUser());
    syncSessionUser();

    window.addEventListener('dp-duel-session-changed', syncSessionUser);
    // For multi-tab scenarios
    window.addEventListener('storage', syncSessionUser);

    return () => {
      window.removeEventListener('dp-duel-session-changed', syncSessionUser);
      window.removeEventListener('storage', syncSessionUser);
    };
  }, []);

  // Initialize notification system and session management
  useEffect(() => {
    try {
      // Always set up notification listener (needed for FCM token registration)
      setupNotificationListener();
      console.log('✅ Notification listener setup completed');
      
      // Initialize session management
      initializeSession();
      console.log('✅ Session management initialized');
      
      // Test security headers (development only)
      testSecurityHeaders();
      
      // Setup notification status monitoring
      setupNotificationStatusMonitoring();
    } catch (error) {
      console.error('❌ Error setting up notification listener:', error);
    }
  }, []); // Run once when app starts

  // Initialize notifications when user session changes
  useEffect(() => {
    const uid = sessionUser?.uid;
    if (uid) {
      console.log('✅ User logged in, initializing notifications');
      
      // Messenger-style: Enhanced notification initialization
      const initializeMessengerStyleNotifications = async () => {
        try {
          // Step 1: Check version and stored tokens
          await checkAndReinitializeNotifications(uid);
          
          // Step 2: Initialize Messenger-style notifications
          const { initializeMessengerStyleNotifications } = await import('./utils/notificationManager');
          const result = await initializeMessengerStyleNotifications(uid);
          
          if (result.success) {
            console.log('✅ Messenger-style notifications initialized successfully');
          } else {
            console.error('❌ Failed to initialize Messenger-style notifications:', result.error);
          }
        } catch (error) {
          console.error('❌ Error initializing Messenger-style notifications:', error);
        }
      };
      
      initializeMessengerStyleNotifications();
    }
  }, [sessionUser?.uid]); // Run when session user becomes available/changes

  // Messenger-style: Periodic token refresh (every 30 minutes when app is active)
  useEffect(() => {
    const tokenRefreshInterval = setInterval(async () => {
      if (!document.hidden) { // Only refresh when app is visible
        const user = getLoggedInUser();
        if (user && user.uid) {
          try {
            const { ensureActiveToken } = await import('./utils/notificationManager');
            await ensureActiveToken(user.uid);
            console.log('🔄 Periodic token refresh completed');
          } catch (error) {
            console.error('❌ Periodic token refresh failed:', error);
          }
        }
      }
    }, 30 * 60 * 1000); // 30 minutes (more frequent than current 4 hours)

    return () => clearInterval(tokenRefreshInterval);
  }, []);

  // Global scroll restoration for mobile WebView issues
  useEffect(() => {
    const restoreScroll = () => {
      console.log('🔄 Global scroll restoration triggered...');
      
      // Fix body and html scroll
      document.body.style.overflow = 'auto';
      document.body.style.touchAction = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.documentElement.style.touchAction = 'auto';
      
      // Fix all scrollable containers
      const scrollableElements = document.querySelectorAll(
        '.tournament-container, .duel-container, .deck-container, .menu-container, .profile-container, .shop-container, .tool-container, .leaderboard-container, .room-container, .pvp-container'
      );
      
      scrollableElements.forEach(element => {
        if (element) {
          element.style.overflow = 'auto';
          element.style.overflowY = 'auto';
          element.style.overflowX = 'hidden';
          element.style.pointerEvents = 'auto';
          element.style.touchAction = 'auto';
          
          // Don't force scroll refresh - this was causing the scroll to jump back to top
          // element.scrollTop = element.scrollTop; // REMOVED THIS LINE
        }
      });
      
      // Special handling for hand bars in duel pages
      const handBars = document.querySelectorAll('.hand-bar, .hand-bar-drag');
      handBars.forEach(handBar => {
        if (handBar) {
          handBar.style.overflowX = 'auto';
          handBar.style.pointerEvents = 'auto';
          handBar.style.touchAction = 'auto';
        }
      });
      
      console.log('✅ Global scroll restored');
    };

    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('📱 App regained focus, restoring global scroll...');
        // Small delay to ensure DOM is ready
        setTimeout(restoreScroll, 100);
        
        // Messenger-style: Refresh FCM token when app resumes
        try {
          const user = getLoggedInUser();
          if (user && user.uid) {
            console.log('🔄 App resumed - refreshing FCM token...');
            
            // Import notification functions
            const { ensureActiveToken } = await import('./utils/notificationManager');
            
            // Force refresh the FCM token
            await ensureActiveToken(user.uid);
            console.log('✅ FCM token refreshed on app resume');
          }
        } catch (error) {
          console.error('❌ Error refreshing FCM token on app resume:', error);
        }
      }
    };

    const handleFocus = () => {
      console.log('🎯 Window focused, restoring global scroll...');
      setTimeout(restoreScroll, 100);
    };

    const handleBlur = () => {
      console.log(' Window blurred');
    };

    // Listen for app visibility changes (when switching between apps)
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listen for window focus/blur events
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    // Listen for iframe load events globally
    const handleIframeLoad = () => {
      console.log('📺 Iframe loaded, ensuring global scroll works...');
      setTimeout(restoreScroll, 200);
    };
    
    // Monitor for new iframes being added
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.tagName === 'IFRAME') {
            node.addEventListener('load', handleIframeLoad);
          }
        });
      });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    // Also handle existing iframes
    const existingIframes = document.querySelectorAll('iframe');
    existingIframes.forEach(iframe => {
      iframe.addEventListener('load', handleIframeLoad);
    });

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      observer.disconnect();
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <NotificationProvider>
        <UnreadMessagesProvider>
          <ToastContainer
            position="bottom-center"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnHover
            draggable
          />
          
          <GlobalNotification />

          <Router>
        <VersionGate>
          <GlobalUnreadBanner />
          <BackButtonHandler />
          <BGMManager />
          <GlobalSettingsModal show={showSettings && !cardSearchOpen} setShow={setShowSettings} />
          {!cardSearchOpen && showGlobalTopButtons && (
            <img
              src={SettingsIcon}
              alt="Settings"
              className="settings-icon"
              onClick={() => setShowSettings(true)}
            />
          )}
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/menu" element={<ProtectedRoute><MenuPage /></ProtectedRoute>} />
            <Route path="/shop" element={<ProtectedRoute><ShopCategoryPage /></ProtectedRoute>} />
            <Route path="/shop/ocg" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
            <Route path="/shop/tcg" element={<ProtectedRoute><ShopPageTCG /></ProtectedRoute>} />
            <Route path="/shop/accessories" element={<ProtectedRoute><ShopPageAccess /></ProtectedRoute>} />
            <Route path="/shop/misc" element={<ProtectedRoute><ShopPageMisc /></ProtectedRoute>} />
            {/* Community Market routes disabled - Coming Soon */}
            {/* <Route path="/marketplace" element={<ProtectedRoute><CommunityMarketPage /></ProtectedRoute>} /> */}
            {/* <Route path="/marketplace/create" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} /> */}
            {/* <Route path="/marketplace/edit/:listingId" element={<ProtectedRoute><EditListingPage /></ProtectedRoute>} /> */}
            {/* <Route path="/marketplace/listing/:listingId" element={<ProtectedRoute><ListingDetailPage /></ProtectedRoute>} /> */}
            {/* <Route path="/marketplace/my-listings" element={<ProtectedRoute><MyListingsPage /></ProtectedRoute>} /> */}
            {/* <Route path="/marketplace/my-purchases" element={<ProtectedRoute><MyPurchasesPage /></ProtectedRoute>} /> */}
            {/* <Route path="/marketplace/my-sales" element={<ProtectedRoute><MySalesPage /></ProtectedRoute>} /> */}
            <Route path="/duelists" element={<ProtectedRoute><DuelistListPage /></ProtectedRoute>} />
            <Route path="/profile/:id" element={<ProtectedRoute><PublicProfilePage /></ProtectedRoute>} />
            <Route path="/tool/time-limit" element={<ProtectedRoute><TimeLimitPage /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><PurchaseHistoryPage /></ProtectedRoute>} />
            <Route path="/tournament" element={<ProtectedRoute><TournamentPage /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
            <Route path="/tool" element={<ProtectedRoute><ToolPage /></ProtectedRoute>} />
            <Route path="/tools/card-search" element={<ProtectedRoute><CardSearch /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><MyProfilePage /></ProtectedRoute>} />
            <Route path="/account-settings" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
            <Route path="/admin-panelx-952504" element={<PrivateAdminRoute><AdminDashboard /></PrivateAdminRoute>} />
            <Route path="/admin-panelx-952504/security-dashboard" element={<PrivateAdminRoute><SecurityComplianceDashboard /></PrivateAdminRoute>} />
            <Route path="/admin/tournament" element={<PrivateAdminRoute><AdminTournamentPage /></PrivateAdminRoute>} />
            <Route path="/admin/tournament-archive" element={<PrivateAdminRoute><AdminTournamentArchive /></PrivateAdminRoute>} />
            <Route path="/admin/tournament-participation" element={<PrivateAdminRoute><AdminTournamentParticipationTracker /></PrivateAdminRoute>} />
            <Route path="/admin/leaderboard-archive" element={<PrivateAdminRoute><AdminLeaderboardArchive /></PrivateAdminRoute>} />
            <Route path="/leaderboard-archives" element={<ProtectedRoute><LeaderboardArchiveListPage /></ProtectedRoute>} />
            <Route path="/leaderboard-archive/:archiveId" element={<ProtectedRoute><LeaderboardArchivePage /></ProtectedRoute>} />
            <Route path="/duel" element={<ProtectedRoute><DuelPage /></ProtectedRoute>} />
            <Route path="/deck" element={<ProtectedRoute><DeckPage /></ProtectedRoute>} />
            <Route path="/deckbuilder" element={<DeckBuilderPage />} />
            <Route path="/room" element={<ProtectedRoute><RoomPage /></ProtectedRoute>} />
            <Route path="/PvpOnline" element={<ProtectedRoute><PvpOnline /></ProtectedRoute>} />
            <Route path="/online-duel/:roomId" element={<ProtectedRoute><PvpOnline /></ProtectedRoute>} />
            <Route path="/player-tournament" element={<ProtectedRoute><PlayerTournamentPage /></ProtectedRoute>} />
            <Route path="/admin/quests" element={<PrivateAdminRoute><AdminQuestPage /></PrivateAdminRoute>} />
            <Route path="/admin/achievements" element={<PrivateAdminRoute><AdminAchievementPage /></PrivateAdminRoute>} />
            <Route path="/admin/user-quests" element={<PrivateAdminRoute><AdminUserQuestPage /></PrivateAdminRoute>} />
            <Route path="/admin/manage-users" element={<PrivateAdminRoute><AdminManageUsers /></PrivateAdminRoute>} />
            <Route path="/admin/manage-purchases" element={<PrivateAdminRoute><AdminManagePurchases /></PrivateAdminRoute>} />
            <Route path="/admin/shop-manager" element={<PrivateAdminRoute><AdminShopManager /></PrivateAdminRoute>} />
            <Route path="/admin/rewards" element={<PrivateAdminRoute><AdminRewardSystem /></PrivateAdminRoute>} />
            <Route path="/admin/dp-purchase-history" element={<PrivateAdminRoute><AdminDpPurchaseHistory /></PrivateAdminRoute>} />
            <Route path="/admin/logs" element={<PrivateAdminRoute><AdminLogViewer /></PrivateAdminRoute>} />
            <Route path="/admin/tournament-notification-test" element={<PrivateAdminRoute><TournamentNotificationTest /></PrivateAdminRoute>} />
            <Route path="/duel-sim" element={<DuelSimulator />} />
            <Route path="/duel-drag" element={<ProtectedRoute><DuelDragPage /></ProtectedRoute>} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />


          </Routes>
        </VersionGate>
        </Router>
        <SessionWarning />
        </UnreadMessagesProvider>
      </NotificationProvider>
    </I18nextProvider>
  );
}

export default App;
